import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Alert,
} from "@mui/material";
import { IBattleShipsGame, IGameResult, IPenaltyItem } from "../ts/Definitions";
import { SelectedSettingsContext } from "../ts/Contexts";

interface BattleShipsDialogProps {
  game: IBattleShipsGame;
  onClose: (result: IGameResult) => void;
  penaltyKeys?: string[];
}

type CellState = "empty" | "ship" | "hit" | "miss";

function generateGrid(gridSize: number, numberOfShips: number): CellState[][] {
  const grid: CellState[][] = Array.from({ length: gridSize }, () =>
    Array<CellState>(gridSize).fill("empty")
  );
  let placed = 0;
  while (placed < numberOfShips) {
    const r = Math.floor(Math.random() * gridSize);
    const c = Math.floor(Math.random() * gridSize);
    if (grid[r][c] === "empty") {
      grid[r][c] = "ship";
      placed++;
    }
  }
  return grid;
}

const BattleShipsDialog: React.FC<BattleShipsDialogProps> = ({ game, onClose, penaltyKeys }) => {
  const settings = useContext(SelectedSettingsContext);
  const { gridSize, numberOfShips, maxAttempts, minNumberOfHits, key, userid, username } = game;
  const [grid, setGrid] = useState<CellState[][]>(() => generateGrid(gridSize, numberOfShips));
  const [hits, setHits] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [finished, setFinished] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const [penalties, setPenalties] = useState<(IPenaltyItem)[]>([]);

  useEffect(() => {
    const newPens = penaltyKeys?.map(key => settings.getPenalty(key)).filter(p => p !== undefined) as IPenaltyItem[] || [];
    setPenalties(newPens);
  }, [penaltyKeys]);

  const handleCellClick = (row: number, col: number) => {
    if (finished || grid[row][col] === "hit" || grid[row][col] === "miss") return;
    setAttempts(a => a + 1);
    setGrid(prev => {
      const newGrid = prev.map(rowArr => [...rowArr]);
      if (prev[row][col] === "ship") {
        newGrid[row][col] = "hit";
        setHits(h => h + 1);
        setMessage("Hit!");
      } else {
        newGrid[row][col] = "miss";
        setMessage("Miss!");
      }
      return newGrid;
    });
  };

  useEffect(() => {
    if (hits >= minNumberOfHits) {
      setFinished(true);
      setMessage("You win! Enough ships have been hit.");
    } else if (attempts >= maxAttempts) {
      setFinished(true);
      setMessage("Game over! Out of attempts.");
    }
  }, [hits, attempts, minNumberOfHits, maxAttempts]);

  const handleClose = () => {
    onClose({
      gameKey: key,
      penaltyCount: hits >= minNumberOfHits ? 0 : minNumberOfHits - hits,
      completed: hits,
      total: numberOfShips,
      totalTime: attempts,
      mistakes: attempts - hits,
      userid,
      username,
      date: new Date(),
    });
  };

  return (
    <Dialog open maxWidth="sm" fullWidth>
      <DialogTitle>Battleships</DialogTitle>
      <DialogContent>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Find and hit at least {minNumberOfHits} ships in {maxAttempts} attempts!
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          {penalties.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Penalty for incorrect answers: <b>{penalties.map(p => p.name).join(", ")}</b>
            </Alert>
          )}
        </Typography>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">Hits: {hits} / {numberOfShips}</Typography>
          <Typography variant="body2">Attempts: {attempts} / {maxAttempts}</Typography>
        </Box>
        <Box sx={{
          maxHeight: "60vh",
          overflow: "auto",
          mb: 2,
        }}>
          <Grid container spacing={1} justifyContent="center">
            {grid.map((rowArr, rowIdx) => (
              <Grid key={rowIdx} container item spacing={1} justifyContent="center">
                {rowArr.map((cell, colIdx) => (
                  <Grid item key={colIdx}>
                    <Paper
                      elevation={2}
                      sx={{
                        width: 36,
                        height: 36,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor:
                          cell === "hit"
                            ? "#43a047"
                            : cell === "miss"
                              ? "#e57373"
                              : "#90caf9",
                        cursor: finished || cell === "hit" || cell === "miss" ? "default" : "pointer",
                        fontWeight: "bold",
                        fontSize: 18,
                        userSelect: "none",
                      }}
                      onClick={() => handleCellClick(rowIdx, colIdx)}
                    >
                      {cell === "hit" ? "🚢" : cell === "miss" ? "✕" : ""}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ))}
          </Grid>
        </Box>
        {message && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" color={finished && hits >= minNumberOfHits ? "success.main" : finished ? "error.main" : "text.primary"}>
              {message}
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {finished && (
          <Button variant="contained" color="primary" onClick={handleClose}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BattleShipsDialog;